import { useEffect, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link2,
  Unlink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  editorClassName?: string;
  'aria-label'?: string;
}

function Toolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link-URL', prev?.startsWith('http') ? prev : 'https://');
    if (url === null) return;
    const t = url.trim();
    if (t === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    let href = t;
    if (!/^https?:\/\//i.test(href)) {
      href = `https://${href}`;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href, target: '_blank', rel: 'noopener noreferrer' }).run();
  };

  const btn = (active: boolean) =>
    cn(
      'h-8 w-8 shrink-0 p-0',
      active && 'bg-card-hover'
    );

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 border-b border-border bg-background/90 px-1.5 py-1 dark:bg-card/80"
      role="toolbar"
      aria-label="Textformatierung"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={btn(editor.isActive('bold'))}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        aria-pressed={editor.isActive('bold')}
        title="Fett"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={btn(editor.isActive('italic'))}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        aria-pressed={editor.isActive('italic')}
        title="Kursiv"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={btn(editor.isActive('underline'))}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        aria-pressed={editor.isActive('underline')}
        title="Unterstrichen"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>
      <span className="mx-0.5 h-5 w-px bg-card-hover dark:bg-muted" aria-hidden />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={btn(editor.isActive('bulletList'))}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        aria-pressed={editor.isActive('bulletList')}
        title="Aufzählung"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={btn(editor.isActive('orderedList'))}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        aria-pressed={editor.isActive('orderedList')}
        title="Nummerierte Liste"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <span className="mx-0.5 h-5 w-px bg-card-hover dark:bg-muted" aria-hidden />
      <Button type="button" variant="ghost" size="sm" className={btn(editor.isActive('link'))} onClick={setLink} title="Link setzen">
        <Link2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 shrink-0 p-0"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive('link')}
        title="Link entfernen"
      >
        <Unlink className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Text eingeben…',
  disabled,
  className,
  editorClassName,
  'aria-label': ariaLabel = 'Rich-Text',
}: RichTextEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Underline,
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-foreground underline underline-offset-2 dark:text-muted',
        },
      }),
    ],
    content: value || '',
    editable: !disabled,
    editorProps: {
      attributes: {
        'aria-label': ariaLabel,
        class: cn(
          'prose-editor max-w-none min-h-[220px] px-3 py-2 pr-12 pb-10 text-sm outline-none',
          'text-foreground',
          'focus-visible:outline-none',
          /* Preflight often strips list markers — restore for TipTap */
          '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-6',
          '[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-6',
          '[&_li]:my-0.5 [&_li]:pl-0.5',
          '[&_p]:my-1',
          '[&_a]:text-foreground [&_a]:underline dark:[&_a]:text-muted',
          disabled && 'cursor-not-allowed opacity-60'
        ),
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || '';
    if (next === current) return;
    editor.commands.setContent(next || '<p></p>', { emitUpdate: false });
  }, [value, editor]);

  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col overflow-hidden rounded-md border border-border bg-card shadow-sm transition-colors',
        '',
        'focus-within:ring-2 focus-within:ring-border focus-within:ring-offset-2',
        disabled && 'opacity-60',
        className
      )}
    >
      <Toolbar editor={editor} />
      <div className={cn('relative min-h-[220px] w-full', editorClassName)}>
        <EditorContent editor={editor} className="w-full [&_.ProseMirror]:min-h-[220px]" />
      </div>
    </div>
  );
}
